import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, ArrowLeftRight, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { PageHeader, StatusBadge, stockState, Empty, Confirm } from "@/components/kit";
import { ProductDialog } from "@/components/ProductDialog";
import { MovementsDialog, MovementRow } from "@/components/MovementsDialog";

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [moves, setMoves] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [onlyLow, setOnlyLow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [movP, setMovP] = useState(null);

  const load = useCallback(() => {
    api.get("/products").then((r) => {
      setProducts(r.data);
      setMovP((m) => (m ? r.data.find((p) => p.id === m.id) || null : m));
    });
    api.get("/movements").then((r) => setMoves(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const categories = useMemo(() => [...new Set(products.map((p) => p.category))].sort(), [products]);
  const filtered = products.filter((p) => {
    const t = q.toLowerCase();
    const okQ = !t || [p.name, p.code, p.description].some((x) => (x || "").toLowerCase().includes(t));
    const st = stockState(p);
    return okQ && (cat === "all" || p.category === cat) && (!onlyLow || st === "bajo" || st === "agotado");
  });
  const lowCount = products.filter((p) => ["bajo", "agotado"].includes(stockState(p))).length;

  const del = async (p) => {
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Artículo eliminado");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader title="Inventario" subtitle={`${products.length} artículos · ${lowCount} con stock bajo`}>
        <Button className="btn-neon" onClick={() => { setEdit(null); setDlg(true); }} data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-1" />Nuevo artículo
        </Button>
      </PageHeader>

      <Tabs defaultValue="products">
        <TabsList className="bg-[#120E2E] border border-[#271E56] mb-4">
          <TabsTrigger value="products" data-testid="tab-products">Productos y servicios</TabsTrigger>
          <TabsTrigger value="moves" data-testid="tab-movements">Movimientos recientes</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="panel p-4 flex flex-col md:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
              <Input className="pl-9" placeholder="Buscar por nombre, código o descripción" value={q} onChange={(e) => setQ(e.target.value)} data-testid="inventory-search-input" />
            </div>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="md:w-56" data-testid="inventory-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant={onlyLow ? "default" : "outline"} onClick={() => setOnlyLow(!onlyLow)} data-testid="inventory-low-filter">
              Solo stock bajo ({lowCount})
            </Button>
          </div>

          <div className="panel overflow-x-auto">
            <table className="w-full text-sm min-w-[880px]" data-testid="inventory-table">
              <thead>
                <tr className="text-left label-cap">
                  <th className="p-4">Artículo</th><th>Categoría</th><th className="text-right">Costo</th>
                  <th className="text-right">Venta</th><th className="text-right">Stock</th><th className="pl-4">Estado</th><th className="text-right pr-4">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const st = stockState(p);
                  return (
                    <tr key={p.id} className={`border-t border-[#271E56] hover:bg-[#1A143D]/60 transition-colors ${st === "bajo" || st === "agotado" ? "bg-amber-500/[0.04]" : ""}`} data-testid={`product-row-${p.code}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {p.image ? <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover" /> : <div className="w-10 h-10 rounded-lg bg-[#1A143D] flex items-center justify-center"><Package className="w-4 h-4 text-slate-500" /></div>}
                          <div className="min-w-0">
                            <div className="text-white font-medium">{p.name}</div>
                            <div className="mono text-xs text-slate-400">{p.code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-slate-300">{p.category}</td>
                      <td className="mono text-right text-slate-400">{money(p.cost_price)}</td>
                      <td className="mono text-right text-white">{money(p.sale_price)}</td>
                      <td className="mono text-right">{p.type === "servicio" ? "—" : <span>{p.stock}<span className="text-slate-500"> / {p.min_stock}</span></span>}</td>
                      <td className="pl-4"><StatusBadge status={st} testId={`stock-badge-${p.code}`} /></td>
                      <td className="pr-4">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" title="Movimientos" onClick={() => setMovP(p)} data-testid={`product-moves-${p.code}`}><ArrowLeftRight className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" title="Editar" onClick={() => { setEdit(p); setDlg(true); }} data-testid={`product-edit-${p.code}`}><Pencil className="w-4 h-4" /></Button>
                          <Confirm title="¿Eliminar artículo?" description={`Se eliminará "${p.name}" del inventario.`} onConfirm={() => del(p)} actionLabel="Eliminar">
                            <Button size="icon" variant="ghost" className="text-rose-400" title="Eliminar" data-testid={`product-delete-${p.code}`}><Trash2 className="w-4 h-4" /></Button>
                          </Confirm>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!filtered.length && <Empty text="No se encontraron artículos" />}
          </div>
        </TabsContent>

        <TabsContent value="moves">
          <div className="panel p-5" data-testid="recent-movements">
            {moves.map((m) => <MovementRow key={m.id} m={m} showProduct />)}
            {!moves.length && <Empty text="Sin movimientos" />}
          </div>
        </TabsContent>
      </Tabs>

      <ProductDialog open={dlg} onOpenChange={setDlg} product={edit} categories={categories} onSaved={load} />
      <MovementsDialog product={movP} onOpenChange={(o) => !o && setMovP(null)} onSaved={load} />
    </div>
  );
}
